import React, { useState } from 'react';
import { Trophy, Plus, X } from 'lucide-react';

const SportsPreferences = ({ preferences, onUpdate }) => {
  const [enabled, setEnabled] = useState(preferences?.enabled || false);
  const [selectedSports, setSelectedSports] = useState(preferences?.types || []);
  const [teams, setTeams] = useState(preferences?.teams || []);
  const [participation, setParticipation] = useState(preferences?.participation || '');
  const [fitnessLevel, setFitnessLevel] = useState(preferences?.fitnessLevel || '');
  const [gymGoer, setGymGoer] = useState(preferences?.gymGoer || false);
  const [outdoorEnthusiast, setOutdoorEnthusiast] = useState(preferences?.outdoorEnthusiast || false);

  // New team form
  const [newTeam, setNewTeam] = useState({ sport: '', team: '', league: '', intensity: '' });
  const [showTeamForm, setShowTeamForm] = useState(false);

  const sportsTypes = [
    // Watching
    'AFL', 'NRL', 'Soccer/Football', 'Cricket', 'Basketball', 'Tennis',
    'Rugby Union', 'Netball', 'Baseball', 'Golf', 'Boxing/MMA',
    'Motor Sports', 'Horse Racing', 'Olympic Sports',
    // Participating
    'Running', 'Gym/Fitness', 'Swimming', 'Cycling', 'Yoga', 'Pilates',
    'Martial Arts', 'Rock Climbing', 'Surfing', 'Hiking', 'CrossFit',
    'Dance', 'Volleyball', 'Badminton', 'Squash', 'Other'
  ];

  const teamSports = ['AFL', 'NRL', 'Soccer', 'Cricket', 'Basketball', 'Rugby Union', 'Other'];
  
  const intensityLevels = [
    'Casual fan',
    'Regular follower',
    'Die-hard supporter'
  ];

  const participationLevels = [
    'Watch only',
    'Play recreationally',
    'Play competitively',
    'Both watch and play'
  ];

  const fitnessLevels = [
    'Not active',
    'Lightly active',
    'Moderately active',
    'Very active',
    'Athlete'
  ];

  const toggleSport = (sport) => {
    setSelectedSports(prev => 
      prev.includes(sport) 
        ? prev.filter(s => s !== sport)
        : [...prev, sport]
    );
  };

  const addTeam = () => {
    if (newTeam.sport && newTeam.team && newTeam.intensity) {
      setTeams([...teams, { ...newTeam }]);
      setNewTeam({ sport: '', team: '', league: '', intensity: '' });
      setShowTeamForm(false);
    }
  };

  const removeTeam = (index) => {
    setTeams(teams.filter((_, i) => i !== index));
  };

  const getIntensityColor = (intensity) => {
    switch(intensity) {
      case 'Casual fan':
        return 'bg-blue-100 text-blue-700';
      case 'Regular follower':
        return 'bg-green-100 text-green-700';
      case 'Die-hard supporter':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const handleSave = async () => {
    const sportsData = {
      enabled,
      types: selectedSports,
      teams,
      participation,
      fitnessLevel,
      gymGoer,
      outdoorEnthusiast
    };

    await onUpdate(sportsData);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Header with toggle */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Trophy className="w-6 h-6 text-blue-500" />
          <h3 className="text-xl font-semibold">Sports & Fitness Preferences</h3>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
        </label>
      </div>

      {enabled && (
        <div className="space-y-6">
          {/* Sports Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Sports & Activities I Enjoy
            </label>
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(120px, 1fr))', gap:8}}>
              {sportsTypes.map(sport => (
                <button
                  key={sport}
                  onClick={() => toggleSport(sport)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                    selectedSports.includes(sport)
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {sport}
                </button>
              ))}
            </div>
          </div>

          {/* Team Affiliations */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Teams I Support
              </label>
              <button
                onClick={() => setShowTeamForm(!showTeamForm)}
                className="text-blue-500 hover:text-blue-600 flex items-center gap-1 text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Team
              </button>
            </div>

            {showTeamForm && (
              <div className="bg-gray-50 p-4 rounded-lg mb-3 space-y-3">
                <select
                  value={newTeam.sport}
                  onChange={(e) => setNewTeam({ ...newTeam, sport: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select sport</option>
                  {teamSports.map(sport => (
                    <option key={sport} value={sport}>{sport}</option>
                  ))}
                </select>

                <input
                  type="text"
                  value={newTeam.team}
                  onChange={(e) => setNewTeam({ ...newTeam, team: e.target.value })}
                  placeholder="Team name (e.g., Brisbane Broncos)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />

                <input
                  type="text"
                  value={newTeam.league}
                  onChange={(e) => setNewTeam({ ...newTeam, league: e.target.value })}
                  placeholder="League (optional, e.g., NRL, A-League)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />

                <select
                  value={newTeam.intensity}
                  onChange={(e) => setNewTeam({ ...newTeam, intensity: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">How passionate are you?</option>
                  {intensityLevels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>

                <div className="flex gap-2">
                  <button
                    onClick={addTeam}
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                  >
                    Add Team
                  </button>
                  <button
                    onClick={() => setShowTeamForm(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {teams.map((team, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-blue-50 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{team.team}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-gray-600">
                        {team.sport} {team.league && `• ${team.league}`}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getIntensityColor(team.intensity)}`}>
                        {team.intensity}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => removeTeam(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Participation Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Participation Level
            </label>
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(100px, 1fr))', gap:8}}>
              {participationLevels.map(level => (
                <button
                  key={level}
                  onClick={() => setParticipation(level)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                    participation === level
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          {/* Fitness Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Fitness Level
            </label>
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(90px, 1fr))', gap:8}}>
              {fitnessLevels.map(level => (
                <button
                  key={level}
                  onClick={() => setFitnessLevel(level)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                    fitnessLevel === level
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          {/* Additional Preferences */}
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:16}}>
            <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
              <input
                type="checkbox"
                checked={gymGoer}
                onChange={(e) => setGymGoer(e.target.checked)}
                className="w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-gray-700">I'm a regular gym-goer</span>
            </label>

            <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
              <input
                type="checkbox"
                checked={outdoorEnthusiast}
                onChange={(e) => setOutdoorEnthusiast(e.target.checked)}
                className="w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-gray-700">I love outdoor activities</span>
            </label>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition font-medium"
            >
              Save Sports Preferences
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SportsPreferences;
